<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { A11y, Autoplay, EffectFade } from 'swiper/modules'
import { IconArrowRight, IconPause, IconPlayArrow } from '@arco-design/web-vue/es/icon'
import 'swiper/css'
import 'swiper/css/effect-fade'

const props = defineProps({
    banner: { type: Array, default: () => [] },
    autoplayDelay: { type: Number, default: 5000 },
    buttontitle: { type: String, default: '' }
})

const swiper = shallowRef(null)
const activeIndex = ref(0)
const progress = ref(0)
const hovered = ref(false)
const focused = ref(false)
const paused = ref(false)
const hidden = ref(false)
const reducedMotion = ref(false)
const failedMedia = ref(new Set())
const navigation = ref(null)
const videos = []
const mediaKey = computed(() => props.banner.map(item => `${item.id}|${item.img}`).join(','))
const stopped = computed(() => hovered.value || focused.value || paused.value || hidden.value || reducedMotion.value)
const isVideo = src => /\.(?:mp4|webm|og[gv]|mov|m4v)(?:[?#].*)?$/i.test(src || '')
let motionQuery

const syncPlayback = () => {
    if (!swiper.value || swiper.value.destroyed) return
    const autoplay = swiper.value.autoplay
    if (stopped.value || props.banner.length < 2) autoplay?.stop()
    else if (autoplay && !autoplay.running) {
        autoplay.start()
        if (autoplay.paused) autoplay.resume()
    }

    videos.forEach((video, index) => {
        if (!video) return
        if (index === activeIndex.value && !stopped.value) video.play()?.catch(() => {})
        else video.pause()
    })
}

const handleSlideChange = instance => {
    activeIndex.value = instance.realIndex
    progress.value = 0
    nextTick(syncPlayback)
}

const handleSwiper = instance => {
    swiper.value = instance
    handleSlideChange(instance)
}

const selectSlide = index => {
    if (index === activeIndex.value) return
    swiper.value?.slideToLoop(index)
}

const handleNavigationKey = (event, index) => {
    const directions = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }
    let target
    if (event.key === 'Home') target = 0
    else if (event.key === 'End') target = props.banner.length - 1
    else if (directions[event.key]) target = (index + directions[event.key] + props.banner.length) % props.banner.length
    else return
    event.preventDefault()
    selectSlide(target)
    navigation.value?.querySelectorAll('button')[target]?.focus()
}

const handleFocusOut = event => {
    focused.value = event.currentTarget.contains(event.relatedTarget)
}
const handleVisibility = () => { hidden.value = document.hidden }
const handleMotion = () => { reducedMotion.value = motionQuery.matches }

watch(stopped, syncPlayback)
watch(activeIndex, async index => {
    await nextTick()
    const nav = navigation.value
    const tab = nav?.querySelectorAll('button')[index]
    if (tab && nav.scrollWidth > nav.clientWidth) nav.scrollLeft = tab.offsetLeft - nav.offsetLeft
})
onMounted(() => {
    motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    handleMotion()
    handleVisibility()
    motionQuery.addListener(handleMotion)
    document.addEventListener('visibilitychange', handleVisibility)
})
onBeforeUnmount(() => {
    motionQuery?.removeListener(handleMotion)
    document.removeEventListener('visibilitychange', handleVisibility)
    videos.forEach(video => video?.pause())
})
</script>

<template>
    <section
        v-if="banner.length"
        class="hz-banner2"
        :aria-label="$t('index.bannerCarousel')"
        :style="{ '--banner-progress': progress }"
        @mouseenter="hovered = true"
        @mouseleave="hovered = false"
        @focusin="focused = true"
        @focusout="handleFocusOut"
    >
        <Swiper
            :key="mediaKey"
            class="hz-banner2__swiper"
            :modules="[A11y, Autoplay, EffectFade]"
            effect="fade"
            :fade-effect="{ crossFade: true }"
            :speed="reducedMotion ? 0 : 600"
            :loop="banner.length > 1"
            :autoplay="banner.length > 1 ? { delay: autoplayDelay, disableOnInteraction: false } : false"
            :a11y="{ slideLabelMessage: '{{index}} / {{slidesLength}}' }"
            @swiper="handleSwiper"
            @slide-change="handleSlideChange"
            @slide-change-transition-end="syncPlayback"
            @autoplay-time-left="(_instance, _time, remaining) => progress = 1 - remaining"
        >
            <SwiperSlide
                v-for="(item, index) in banner"
                :key="`${item.id}-${index}`"
                class="hz-banner2__slide"
                :aria-hidden="index !== activeIndex"
            >
                <template v-if="item.img && !failedMedia.has(item.img)">
                    <video
                        v-if="isVideo(item.img)"
                        :ref="element => videos[index] = element"
                        class="hz-banner2__media"
                        :src="item.img"
                        :poster="item.poster || undefined"
                        muted
                        loop
                        playsinline
                        preload="metadata"
                        aria-hidden="true"
                        @loadeddata="syncPlayback"
                        @error="failedMedia.add(item.img)"
                    />
                    <img
                        v-else
                        class="hz-banner2__media"
                        :src="item.img"
                        alt=""
                        :loading="index === 0 ? 'eager' : 'lazy'"
                        @error="failedMedia.add(item.img)"
                    />
                </template>
                <div class="hz-banner2__wash" aria-hidden="true"></div>
                <div class="hz-banner2__inner Hezi-width">
                    <div class="hz-banner2__content">
                        <div class="hz-banner2__copy">
                            <h2 class="hz-banner2__title">{{ item.title }}</h2>
                            <p v-if="item.details" class="hz-banner2__description">{{ item.details }}</p>
                        </div>
                        <a
                            v-if="item.url"
                            class="hz-banner2__action"
                            :href="item.url"
                            :tabindex="index === activeIndex ? 0 : -1"
                        >
                            <span>{{ buttontitle || $t('index.bannerButton') }}</span>
                            <IconArrowRight aria-hidden="true" />
                        </a>
                    </div>
                </div>
            </SwiperSlide>
        </Swiper>

        <div v-if="banner.length > 1" class="hz-banner2__navigation-frame">
            <nav ref="navigation" class="hz-banner2__navigation" :aria-label="$t('index.bannerNavigation')">
                <button
                    v-for="(item, index) in banner"
                    :key="`${item.id}-${index}`"
                    type="button"
                    class="hz-banner2__tab"
                    :class="{ 'is-active': activeIndex === index }"
                    :aria-current="activeIndex === index ? 'true' : undefined"
                    :title="[item.small, item.title].filter(Boolean).join(' ')"
                    @click="selectSlide(index)"
                    @keydown="handleNavigationKey($event, index)"
                >
                    <span v-if="item.small" class="hz-banner2__badge">{{ item.small }}</span>
                    <span class="hz-banner2__tab-title">{{ item.title }}</span>
                    <span v-if="activeIndex === index" class="hz-banner2__progress" aria-hidden="true"></span>
                </button>
            </nav>
        </div>

        <button
            v-if="banner.length > 1 && !reducedMotion"
            type="button"
            class="hz-banner2__playback"
            :aria-label="$t(paused ? 'index.bannerPlay' : 'index.bannerPause')"
            :title="$t(paused ? 'index.bannerPlay' : 'index.bannerPause')"
            @click="paused = !paused"
        >
            <IconPlayArrow v-if="paused" aria-hidden="true" />
            <IconPause v-else aria-hidden="true" />
        </button>
    </section>
</template>

<style scoped lang="less">
.hz-banner2 {
    --banner-blue: #0052d9;
    --banner-surface: #edf6ff;
    --banner-ink: #202124;
    --banner-muted: #495b76;
    --banner-nav-width: 14rem;
    --banner-gap: 3rem;
    position: relative;
    width: 100%;
    color: var(--banner-ink);
    background: var(--banner-surface);
    font-family: Arial, 'Microsoft YaHei', 'PingFang SC', sans-serif;
}

.hz-banner2__swiper {
    width: 100%;
}

.hz-banner2__slide {
    position: relative;
    min-height: 36rem;
    height: auto;
    background: var(--banner-surface);
}

.hz-banner2__media,
.hz-banner2__wash {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
}

.hz-banner2__media {
    object-fit: cover;
    object-position: center;
}

.hz-banner2__wash {
    background: linear-gradient(90deg, rgba(245, 250, 255, .97) 0%, rgba(245, 250, 255, .9) 35%, rgba(237, 246, 255, .18) 64%, transparent 80%);
}

.hz-banner2__inner,
.hz-banner2__navigation-frame {
    width: calc(100% - 20rem);
    max-width: 100rem;
    margin: 0 auto;
}

.hz-banner2__inner {
    position: relative;
    flex: 1;
    display: flex;
}

.hz-banner2__content {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
    min-height: 36rem;
    padding: 8rem 0 5rem;
    margin-left: calc(var(--banner-nav-width) + var(--banner-gap));
}

.hz-banner2__copy {
    width: 100%;
    max-width: 38rem;
}

.hz-banner2__title {
    margin: 0;
    color: var(--banner-ink);
    font-size: 2rem;
    font-weight: 400;
    line-height: 1.35;
    overflow-wrap: anywhere;
}

.hz-banner2__description {
    margin: 1rem 0 2rem;
    color: var(--banner-muted);
    font-size: max(14px, .9rem);
    line-height: 1.7;
    white-space: pre-line;
    overflow-wrap: anywhere;
}

.hz-banner2__action {
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    width: 20rem;
    max-width: 100%;
    min-height: 48px;
    padding: .8rem 1rem;
    color: #fff;
    background: linear-gradient(100deg, var(--banner-blue), #2878ff);
    font-size: max(14px, .9rem);
    text-decoration: none;
    cursor: pointer;

    .arco-icon { font-size: 22px; }
    &:hover { filter: brightness(1.08); }
    &:active { filter: brightness(.94); }
}

.hz-banner2__navigation-frame {
    position: absolute;
    inset: 6.5rem 0 7rem;
    z-index: 2;
    pointer-events: none;
}

.hz-banner2__navigation {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: var(--banner-nav-width);
    overflow-y: auto;
    border-right: 1px solid rgba(255, 255, 255, .9);
    pointer-events: auto;
    scrollbar-width: thin;
    scrollbar-color: #aabbd0 transparent;
}

.hz-banner2__tab {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1 0 auto;
    gap: .5rem;
    width: 100%;
    min-height: 56px;
    padding: .8rem 1.25rem .8rem .6rem;
    border: 0;
    border-right: 2px solid transparent;
    background: transparent;
    color: var(--banner-ink);
    font: inherit;
    font-size: max(14px, .9rem);
    line-height: 1.5;
    text-align: right;
    cursor: pointer;

    &:hover,
    &.is-active { color: var(--banner-blue); background: rgba(255, 255, 255, .24); }
    &.is-active { border-right-color: var(--banner-blue); }
    &:active { background: rgba(255, 255, 255, .65); }
}

.hz-banner2__tab-title {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.hz-banner2__badge {
    flex: 0 0 auto;
    max-width: 4em;
    padding: 0 .35rem;
    overflow: hidden;
    color: #fff;
    background: #0878ee;
    border-radius: 2px;
    font-size: max(12px, .75rem);
    text-overflow: ellipsis;
    white-space: nowrap;
}

.hz-banner2__progress {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 2px;
    background: rgba(0, 82, 217, .22);
    transform: scaleX(var(--banner-progress, 0));
    transform-origin: left;
    transition: none;
}

.hz-banner2__playback {
    position: absolute;
    right: 2rem;
    bottom: 5rem;
    z-index: 2;
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border: 1px solid rgba(73, 91, 118, .25);
    border-radius: 50%;
    color: var(--banner-muted);
    background: rgba(255, 255, 255, .55);
    cursor: pointer;

    &:hover { color: var(--banner-blue); background: #fff; }
    &:active { background: #dcecff; }
}

.hz-banner2 a:focus-visible,
.hz-banner2 button:focus-visible {
    outline: 2px solid var(--banner-blue);
    outline-offset: -3px;
}

@media (min-width: 769px) and (max-width: 1100px) {
    .hz-banner2 { --banner-nav-width: 14rem; --banner-gap: 2rem; }
}

@media (max-width: 768px) {
    .hz-banner2__slide { min-height: 500px; }
    .hz-banner2__inner { width: calc(100% - 40px); }
    .hz-banner2__content { min-height: 500px; margin-left: 0; padding: 38px 0 112px; }
    .hz-banner2__copy { max-width: 100%; }
    .hz-banner2__title { font-size: clamp(26px, 6.5vw, 38px); }
    .hz-banner2__description { margin: 16px 0 32px; font-size: 14px; }
    .hz-banner2__action { width: 240px; padding: 12px 16px; font-size: 16px; }
    .hz-banner2__media { object-position: 65% center; }
    .hz-banner2__wash { background: linear-gradient(110deg, rgba(245, 250, 255, .96), rgba(245, 250, 255, .65) 65%, rgba(237, 246, 255, .2)); }
    .hz-banner2__navigation-frame { inset: auto 20px 20px; width: auto; margin: 0; }
    .hz-banner2__navigation { flex-direction: row; justify-content: flex-start; width: 100%; height: auto; overflow-x: auto; border-right: 0; border-bottom: 1px solid rgba(255, 255, 255, .9); }
    .hz-banner2__tab { flex: 0 0 auto; justify-content: center; width: auto; max-width: 260px; min-height: 52px; padding: 12px; border-right: 0; border-bottom: 2px solid transparent; font-size: 14px; }
    .hz-banner2__tab.is-active { border-bottom-color: var(--banner-blue); }
    .hz-banner2__badge { font-size: 12px; }
    .hz-banner2__playback { right: 20px; bottom: 114px; }
}

@media (prefers-reduced-motion: reduce) {
    .hz-banner2 *,
    .hz-banner2 :deep(.swiper-wrapper) { transition: none !important; }
    .hz-banner2__progress { display: none; }
}
</style>
